/**
 * NA-437: destination-lesson picker for "Copy to another lesson" — search by
 * ID or title, exact-id matches ranked first, published lessons shown but
 * disabled, client-account-only lessons and the current lesson excluded
 * (all enforced server-side by useLessonCopyTargets' listLessons flags).
 *
 * Pagination mechanics (debounced search, infinite-scroll) mirror
 * QuestionCombobox.tsx. Unlike that combobox, the dropdown here renders
 * inline (a plain in-flow child, not a portal) — the approved prototype's
 * `.cblist` is a normal document-flow element right under the input, so
 * opening it grows the modal body (which scrolls via its own
 * `overflow-y:auto`) instead of floating a fixed overlay on top of the
 * modal's other fields. That matches `CopyBlockToLessonModal`'s `.body`,
 * which is deliberately structured the same way as the prototype's
 * `.m-body`, so no portal or viewport-relative positioning is needed.
 *
 * Visual styling (colors, spacing, the fixed-width id column, the badge, the
 * chevron toggle, the "chosen" input state) is an exact match of the approved
 * prototype's `.combo`/`.cblist`/`.copt` rules
 * (https://copy-block-to-lesson.netlify.app/) — literal hex values below are
 * lifted directly from that stylesheet.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { useLessonCopyTargets, type LessonListResponseItem } from '../api/courseRecordApi';
import { formatLessonHumanId } from '../utils/humanIds';

const SEARCH_DEBOUNCE_MS = 300;
const DROPDOWN_MAX_HEIGHT = 236;

export interface LessonCopyComboboxProps {
  /** The lesson currently open — excluded from results and never a valid destination. */
  currentLessonId: string;
  /** Selecting a lesson clears the input's free-text search and shows "ID · Title" instead. */
  selected: LessonListResponseItem | null;
  onSelect: (lesson: LessonListResponseItem) => void;
}

export function LessonCopyCombobox({
  currentLessonId,
  selected,
  onSelect,
}: LessonCopyComboboxProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // The modal's shared focus trap (useModalFocusTrap) auto-focuses the first
  // focusable element on open, which is this input — without this guard that
  // programmatic focus would immediately pop the dropdown open, unlike the
  // prototype (whose modal never auto-focuses the field at all). Skips only
  // the very first focus event; a real click flips it via onMouseDown before
  // focus fires, and every focus after the first one behaves normally.
  const skipNextAutoFocusRef = useRef(true);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const {
    data,
    isLoading,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useLessonCopyTargets(debouncedSearch.trim(), currentLessonId);
  const lessons = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!(containerRef.current?.contains(target) ?? false)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = dropdownRef.current;
    if (
      !isOpen
      || !sentinel
      || !root
      || isFetchNextPageError
      || typeof IntersectionObserver === 'undefined'
    ) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0]?.isIntersecting
        && hasNextPage
        && !isFetchingNextPage
        && !isFetchNextPageError
      ) {
        void fetchNextPage();
      }
    }, { root, threshold: 0.1 });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage, isOpen]);

  const hasDebouncedTerm = debouncedSearch.trim() !== '';
  const isChosen = Boolean(selected) && !isOpen;

  const styles = useMemo((): Record<string, CSSProperties> => ({
    container: {
      position: 'relative',
      width: '100%',
    },
    iwrap: {
      position: 'relative',
    },
    input: {
      width: '100%',
      border: '1px solid #dfe3e8',
      borderRadius: '6px',
      padding: isChosen ? '8px 34px 8px 32px' : '8px 34px 8px 10px',
      fontSize: '14px',
      color: '#1f2933',
      outline: 'none',
      backgroundColor: isChosen ? '#eef2fe' : '#ffffff',
      fontWeight: isChosen ? 600 : 400,
      boxSizing: 'border-box',
      fontFamily: 'inherit',
    },
    pick: {
      position: 'absolute',
      left: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      color: '#1f4fd8',
      pointerEvents: 'none',
      fontSize: '13px',
    },
    chev: {
      position: 'absolute',
      right: '6px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '24px',
      height: '24px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6b7480',
      borderRadius: '4px',
      padding: 0,
      fontSize: '12px',
      transition: 'transform 0.15s',
    },
    dropdown: {
      marginTop: '5px',
      backgroundColor: '#ffffff',
      border: '1px solid #dfe3e8',
      borderRadius: '8px',
      boxShadow: '0 6px 18px rgba(20, 25, 32, 0.08)',
      maxHeight: `${DROPDOWN_MAX_HEIGHT}px`,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
    lessonRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      textAlign: 'left',
      background: 'none',
      border: 'none',
      borderBottom: '1px solid #eceef1',
      padding: '8px 12px',
      fontFamily: 'inherit',
      fontSize: '14px',
      color: '#1f2933',
      cursor: 'pointer',
    },
    lessonRowSelected: {
      backgroundColor: '#eef2fe',
    },
    lessonRowDisabled: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      textAlign: 'left',
      background: 'none',
      border: 'none',
      borderBottom: '1px solid #eceef1',
      padding: '8px 12px',
      fontFamily: 'inherit',
      fontSize: '14px',
      color: '#1f2933',
      cursor: 'default',
      opacity: 0.5,
    },
    lid: {
      flexShrink: 0,
      width: '42px',
      fontSize: '12.5px',
      color: '#6b7480',
      fontVariantNumeric: 'tabular-nums',
    },
    nm: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    badge: {
      flexShrink: 0,
      fontSize: '11px',
      fontWeight: 600,
      padding: '2px 7px',
      borderRadius: '10px',
      backgroundColor: '#f4f5f6',
      color: '#6b7480',
      whiteSpace: 'nowrap',
    },
    tick: {
      width: '14px',
      flexShrink: 0,
      color: '#1f4fd8',
      display: 'flex',
      fontSize: '12px',
    },
    mutedRow: {
      padding: '18px 12px',
      textAlign: 'center',
      fontFamily: 'inherit',
      fontSize: '13.5px',
      color: '#6b7480',
    },
    loadMoreButton: {
      display: 'block',
      width: '100%',
      border: 'none',
      borderTop: '1px solid #eceef1',
      padding: '8px 12px',
      backgroundColor: '#ffffff',
      color: '#1f4fd8',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '13.5px',
    },
    retryButton: {
      border: 'none',
      background: 'none',
      color: '#1f4fd8',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '13.5px',
      textDecoration: 'underline',
      padding: 0,
    },
    sentinel: {
      height: '1px',
    },
  }), [isChosen]);

  const handleSelect = (lesson: LessonListResponseItem) => {
    if (lesson.workflowState === 'published') return;
    setIsOpen(false);
    setSearch('');
    onSelect(lesson);
  };

  const displayValue = isOpen
    ? search
    : selected
      ? `${formatLessonHumanId(selected.numericId)} · ${selected.title}`
      : search;

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={styles.iwrap}>
        {isChosen && (
          <span style={styles.pick} aria-hidden="true">
            <FontAwesomeIcon icon={faCheck} />
          </span>
        )}
        <input
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-label="Search for a destination lesson"
          placeholder="Enter lesson ID or title"
          value={displayValue}
          onChange={(event) => {
            skipNextAutoFocusRef.current = false;
            setSearch(event.target.value);
            setIsOpen(true);
          }}
          onMouseDown={() => {
            // A click always opens, even when the input is already focused
            // (e.g. left focused by the modal's auto-focus on open) — in that
            // case no 'focus' event fires at all, so onFocus alone can't
            // handle it. Guarded by `isOpen` so clicking to reposition the
            // caret in already-open, already-typed text doesn't wipe it.
            skipNextAutoFocusRef.current = false;
            if (!isOpen) {
              setSearch('');
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            if (skipNextAutoFocusRef.current) {
              skipNextAutoFocusRef.current = false;
              return;
            }
            setSearch('');
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setIsOpen(false);
            }
          }}
          style={styles.input}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          style={{ ...styles.chev, transform: `translateY(-50%) rotate(${isOpen ? 180 : 0}deg)` }}
          onClick={() => setIsOpen((open) => !open)}
          onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = '#f4f5f6'; }}
          onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
      </div>

      {isOpen && (
        <div ref={dropdownRef} style={styles.dropdown} role="listbox" aria-label="Lessons">
          {isLoading ? (
            <div style={styles.mutedRow} role="status" aria-live="polite">Loading lessons…</div>
          ) : isError && lessons.length === 0 ? (
            <div style={styles.mutedRow} role="status">
              Failed to load lessons.{' '}
              <button type="button" style={styles.retryButton} onClick={() => { void refetch(); }}>
                Retry
              </button>
            </div>
          ) : lessons.length === 0 && hasDebouncedTerm ? (
            <div style={styles.mutedRow} role="status">No matching lessons found.</div>
          ) : lessons.length === 0 ? (
            <div style={styles.mutedRow} role="status">No lessons available.</div>
          ) : (
            <>
              {lessons.map((lesson) => {
                const isPublished = lesson.workflowState === 'published';
                const isSelected = selected?.id === lesson.id;
                return isPublished ? (
                  <div key={lesson.id} style={styles.lessonRowDisabled} aria-disabled="true">
                    <span style={styles.lid}>{formatLessonHumanId(lesson.numericId)}</span>
                    <span style={styles.nm}>{lesson.title}</span>
                    <span style={styles.badge}>Published</span>
                  </div>
                ) : (
                  <button
                    key={lesson.id}
                    type="button"
                    style={{ ...styles.lessonRow, ...(isSelected ? styles.lessonRowSelected : {}) }}
                    aria-selected={isSelected}
                    onClick={() => handleSelect(lesson)}
                    onMouseEnter={(event) => {
                      if (!isSelected) event.currentTarget.style.backgroundColor = '#f4f5f6';
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.backgroundColor = isSelected ? '#eef2fe' : 'transparent';
                    }}
                  >
                    <span style={styles.lid}>{formatLessonHumanId(lesson.numericId)}</span>
                    <span style={styles.nm}>{lesson.title}</span>
                    {isSelected && (
                      <span style={styles.tick} aria-hidden="true">
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                    )}
                  </button>
                );
              })}

              {isFetchNextPageError && (
                <div style={styles.mutedRow} role="status">
                  Failed to load more lessons.{' '}
                  <button type="button" style={styles.retryButton} onClick={() => { void fetchNextPage(); }}>
                    Retry
                  </button>
                </div>
              )}

              {hasNextPage ? (
                <>
                  <div ref={sentinelRef} style={styles.sentinel} aria-hidden="true" />
                  <button
                    type="button"
                    style={styles.loadMoreButton}
                    onClick={() => { void fetchNextPage(); }}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? 'Loading more lessons…' : 'Load more'}
                  </button>
                </>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

LessonCopyCombobox.displayName = 'LessonCopyCombobox';
